import React, { memo } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { privacyPolicyQueries } from '../api/queries/privacyPolicy';

interface TeacherProtectedRouteProps {
  children: React.ReactNode;
}

const TeacherProtectedRoute: React.FC<TeacherProtectedRouteProps> = memo(({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const policyQuery = useQuery({
    ...privacyPolicyQueries.sync(user?.id),
    enabled: !!user && isAuthenticated && user?.role === 'TEACHER',
  });

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (user?.role !== 'TEACHER') {
    // Redirect to appropriate dashboard based on role
    const redirectPath = user?.role === 'ADMIN' ? '/admin/platform-overview' : '/auth/login';
    return <Navigate to={redirectPath} replace />;
  }

  if (policyQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (policyQuery.data?.requiresAcceptance) {
    return (
      <Navigate
        to="/auth/privacy-policy"
        replace
        state={{ nextPath: location.pathname }}
      />
    );
  }

  return <>{children}</>;
});

TeacherProtectedRoute.displayName = 'TeacherProtectedRoute';

export default TeacherProtectedRoute;
